import styles from './WorkflowCanvas.module.css';
import type { MappingRow } from './types';

interface InputMappingEditorProps {
  rows: MappingRow[];
  onChange: (rows: MappingRow[]) => void;
  /** Optional: known parameter names to pre-populate as row keys */
  knownParams?: string[];
  /** Optional: override the section title */
  title?: string;
}

const TOKEN_SUGGESTIONS = [
  '{{context.userId}}',
  '{{context.sessionId}}',
  '{{trigger.payload.text}}',
  '{{trigger.payload.email}}',
  '{{trigger.payload.phone}}',
  '{{steps.0.output.result}}',
];

export function InputMappingEditor({ rows, onChange, knownParams = [], title = 'Input Mapping' }: InputMappingEditorProps) {
  function updateRow(index: number, field: 'key' | 'value', val: string) {
    const next = rows.map((r, i) => (i === index ? { ...r, [field]: val } : r));
    onChange(next);
  }

  function deleteRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  function addRow() {
    onChange([...rows, { key: '', value: '' }]);
  }

  function injectToken(index: number, token: string) {
    const current = rows[index]?.value ?? '';
    updateRow(index, 'value', current + token);
  }

  function autoPopulate() {
    const existingKeys = new Set(rows.map(r => r.key));
    const newRows = [...rows];
    knownParams.forEach(param => {
      if (!existingKeys.has(param)) {
        newRows.push({ key: param, value: '' });
      }
    });
    onChange(newRows);
  }

  return (
    <div className={styles.mappingSection}>
      <div className={styles.mappingHeader}>
        <span className={styles.mappingTitle}>{title}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          {knownParams.length > 0 && (
            <button
              className={styles.addMappingBtn}
              onClick={autoPopulate}
              type="button"
            >
              ⚡ Auto-populate Schema
            </button>
          )}
          <button className={styles.addMappingBtn} onClick={addRow} type="button">
            + Add Row
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className={styles.emptyMapping}>
          No mappings. Click "+ Add Row" to map parameters to context variables or literal values.
        </p>
      ) : (
        <div className={styles.mappingTable}>
          {rows.map((row, i) => (
            <div key={i} className={styles.mappingRow}>
              <div style={{ position: 'relative', width: '38%', flexShrink: 0 }}>
                <input
                  list={`params-list-${i}`}
                  className={`${styles.fieldInput} ${styles.mappingKey}`}
                  placeholder={knownParams[i] ?? 'param_name'}
                  value={row.key}
                  onChange={(e) => updateRow(i, 'key', e.target.value)}
                  aria-label="Mapping key"
                  style={{ width: '100%' }}
                />
                {knownParams.length > 0 && (
                  <datalist id={`params-list-${i}`}>
                    {knownParams.map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                )}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <input
                  className={`${styles.fieldInput} ${styles.mappingValue}`}
                  placeholder='{{context.userId}} or "literal"'
                  value={row.value}
                  onChange={(e) => updateRow(i, 'value', e.target.value)}
                  aria-label="Mapping value"
                />
                <div className={styles.tokenChips}>
                  {TOKEN_SUGGESTIONS.slice(0, 3).map((tok) => (
                    <button
                      key={tok}
                      className={styles.tokenChip}
                      onClick={() => injectToken(i, tok)}
                      type="button"
                      title={`Insert ${tok}`}
                    >
                      {tok}
                    </button>
                  ))}
                </div>
              </div>
              <button
                className={styles.mappingDeleteBtn}
                onClick={() => deleteRow(i)}
                type="button"
                aria-label="Remove mapping row"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
